import type {
  IIssue,
  IMinimalSafetyEventForClient,
} from '@d-orca/shared/trusstor';
import type { ISafetyEventDetailResponse } from '@d-orca/shared/safety';
import {
  createIssuesApi,
  createMonitorApi,
  parseStandardErrors,
  SiteNotLinkedError,
  UpstreamError,
} from '@d-orca/shared/client/apis';
import { authedFetch } from '@apis/auth';

// The error classes and the failure mapping live beside the shared factory so
// an `instanceof` check here matches what the factory throws; re-exported to
// keep this module's import sites.
export { SiteNotLinkedError, UpstreamError };

// The shared monitor API bound to the web transport (cookie `authedFetch`);
// mobile binds the same factory to its Bearer transport.
export const { fetchMonitorSnapshot, fetchMonitorReplay } = createMonitorApi(authedFetch);

// Issues and the issue form's site catalogs — the shared factory bound to the
// web transport; mobile binds the same factory to its Bearer transport.
export const issuesApi = createIssuesApi(authedFetch);
export const {
  fetchSiteIssues,
  fetchSiteProfessions,
  fetchSiteUsers,
  fetchAreas,
  createIssueApi,
  updateIssueApi,
  deleteIssueApi,
  addTextCommentApi,
  fetchAttachmentBlob,
} = issuesApi;

/** Upload an image as a comment on an issue; returns the updated issue. */
export function addImageCommentApi(siteId: string, issueId: string, file: File): Promise<IIssue> {
  const form = new FormData();
  form.append('file', file);
  return issuesApi.addImageCommentApi(siteId, issueId, form);
}

/** One safety event's detail for the alert dialog (the stream never carries
 *  tag / area), plus whether the caller may end it. */
export async function fetchSafetyEventDetail(
  siteId: string,
  eventId: string,
  signal?: AbortSignal,
): Promise<ISafetyEventDetailResponse> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/monitor/safety-events/${encodeURIComponent(eventId)}`,
    { signal },
  );
  if (res.ok) return (await res.json()) as ISafetyEventDetailResponse;
  return parseStandardErrors(res);
}

/** End Event — closes the safety event upstream; resolves with it closed, or
 *  null when upstream's reply was unreadable (the stream still echoes it). */
export async function closeSafetyEventApi(
  siteId: string,
  eventId: string,
): Promise<{ event: IMinimalSafetyEventForClient | null }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/monitor/safety-events/${encodeURIComponent(eventId)}/close`,
    { method: 'PUT' },
  );
  if (res.ok) return (await res.json()) as { event: IMinimalSafetyEventForClient | null };
  return parseStandardErrors(res);
}
